import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccesEntreprisePanel } from '../components/AccesEntreprisePanel';
import {
  invitationsEmployeur,
  libelleDuRole,
  type MonEmployeur,
} from '@/services/invitationsEmployeur.service';

/**
 * « Mon entreprise » — l'espace de la personne qui travaille CHEZ le client.
 *
 * Pourquoi une page, et pas un onglet de plus sur la fiche de dossier
 * -------------------------------------------------------------------
 * L'écran d'invitation avait d'abord été posé sur la fiche de dossier, parce
 * que c'est le seul endroit du produit où un employeur apparaît. Le jouer a
 * montré que c'était le mauvais endroit, et pour une raison nette :
 *
 *   - le gestionnaire du cabinet OUVRE la fiche mais n'a pas le droit
 *     d'inviter (la matrice de C2 réserve cela à l'administrateur de
 *     l'entreprise) ;
 *   - la dirigeante A le droit d'inviter mais ne peut pas ouvrir la fiche —
 *     elle relève de son employeur, pas du domaine du cabinet.
 *
 * L'écran était donc visible par qui ne peut pas s'en servir, et utilisable
 * par qui ne peut pas le voir. La règle de Rose du 16/08 autorise une entrée
 * de menu pour un TERRITOIRE — un ensemble de travail avec sa propre vie et
 * ses propres objets. « Mon entreprise » en est un : c'est le seul endroit du
 * produit qui appartienne à l'entreprise cliente et non au cabinet.
 *
 * L'entrée n'apparaît que pour qui a une entreprise : un gestionnaire de
 * cabinet ne voit pas un menu vide.
 */
export function MonEntreprisePage() {
  const [parametres] = useSearchParams();
  const [employeurs, setEmployeurs] = useState<MonEmployeur[] | null>(null);
  const [choisi, setChoisi] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const liste = await invitationsEmployeur.miens().catch(() => []);
      setEmployeurs(liste);
      const demande = parametres.get('entreprise');
      const selection = liste.find(employeur => employeur.uuid === demande);
      if (selection) setChoisi(selection.uuid);
      else if (liste.length > 0) setChoisi(liste[0].uuid);
    })();
  }, [parametres]);

  if (employeurs === null) {
    return (
      <div className="mx-auto max-w-5xl p-6 text-sm text-neutral-500">
        Chargement…
      </div>
    );
  }

  if (employeurs.length === 0) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <h1 className="text-2xl font-bold text-neutral-900">Mon entreprise</h1>
        <p className="mt-2 text-sm text-neutral-500">
          Vous ne tenez aucun rôle dans une entreprise. Cet espace est celui des
          personnes invitées par leur direction.
        </p>
      </div>
    );
  }

  const actif = employeurs.find(e => e.uuid === choisi) ?? employeurs[0];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">{actif.nom}</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Votre rôle : {libelleDuRole(actif.role).toLowerCase()}.
          </p>
        </div>

        {/* Plusieurs entreprises est le cas rare : le sélecteur n'apparaît que
            là. Une liste déroulante à un seul choix est du bruit. */}
        {employeurs.length > 1 && (
          <select
            value={actif.uuid}
            onChange={e => setChoisi(e.target.value)}
            aria-label="Choisir l'entreprise"
            className="rounded-lg border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--marque-trait)' }}
          >
            {employeurs.map(e => (
              <option key={e.uuid} value={e.uuid}>
                {e.nom}
              </option>
            ))}
          </select>
        )}
      </div>

      <AccesEntreprisePanel employerUuid={actif.uuid} />
    </div>
  );
}
